import { normalizeOrgId } from "@/features/esg/utils/org-scope.utils";

export const isHoistedNode = (node) => node?.isGroup;

/**
 * Convert a UI org selection to an API orgId.
 * When the selected org matches the company (parent company), returns null.
 */
export const toApiOrgId = (selectedOrgId, companyId) => {
  if (!selectedOrgId || selectedOrgId === "all") return undefined;
  if (companyId && selectedOrgId === companyId) return null;
  return selectedOrgId;
};

export const stripGroupNodes = (nodes = []) =>
  nodes.flatMap((node) => {
    const children = Array.isArray(node?.children) ? node.children : [];
    const processedChildren = stripGroupNodes(children);
    if (isHoistedNode(node)) return processedChildren;
    return [{ ...node, children: processedChildren }];
  });

// Walk an org tree forest and collect ids of selectable nodes (have an
// id, not a hoisted-group placeholder). Used by callers that need to
// know "is there only one effective choice in this scope, and what's
// its id" — e.g. the data-entry filter, which auto-selects when the
// disclosure scope resolves to a single org.
export const collectSelectableOrgIds = (nodes = []) => {
  const ids = [];
  for (const node of nodes) {
    if (node?.id && !isHoistedNode(node)) ids.push(node.id);
    if (node?.children?.length) {
      ids.push(...collectSelectableOrgIds(node.children));
    }
  }
  return ids;
};

export const countSelectableOrgNodes = (nodes = []) =>
  collectSelectableOrgIds(nodes).length;

export const filterOrgTreeByIds = (nodes = [], allowedSet) => {
  const filterNode = (node) => {
    const nodeId = normalizeOrgId(node?.id);
    const children = Array.isArray(node?.children) ? node.children : [];
    const filteredChildren = children.flatMap(filterNode).filter(Boolean);
    if (isHoistedNode(node)) {
      return filteredChildren;
    }
    if (nodeId && allowedSet.has(nodeId)) {
      return [{ ...node, children: filteredChildren }];
    }
    if (filteredChildren.length > 0) {
      return [{ ...node, isGroup: true, children: filteredChildren }];
    }
    return [];
  };
  return nodes.flatMap(filterNode).filter(Boolean);
};

export const makeTreewithAllowOrgIds = (nodes = [], allowedSet = new Set()) => {
  const buildTree = (node) => {
    const nodeId = normalizeOrgId(node?.id);

    const children = Array.isArray(node?.children) ? node.children : [];

    const filteredChildren = children.map(buildTree).filter(Boolean);

    const isAllowed = nodeId && allowedSet.has(nodeId);

    // Keep allowed organization
    if (isAllowed) {
      return {
        ...node,
        children: filteredChildren,
      };
    }

    // Keep parent nodes if they contain allowed children
    if (filteredChildren.length > 0) {
      return {
        ...node,
        isGroup: true,
        children: filteredChildren,
      };
    }

    return null;
  };

  return nodes.map(buildTree).filter(Boolean);
};
